/**
 * SectionPresentationMode
 * Fullscreen slideshow presentation of section content.
 * Displays sections as slides with keyboard navigation.
 */

import { useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import { ChevronLeft, ChevronRight, X, Volume2 } from "lucide-react";
import { Button } from "@learn-me/ui";
import { ContentRenderer, type Section } from "./SectionPlayer";
import { AudioPlayer } from "./AudioPlayer";
import { DiagramRenderer } from "../DiagramRenderer";
import { useQuery } from "@tanstack/react-query";
import { diagramsApi } from "@/lib/diagramsApi";

interface SectionPresentationModeProps {
  sections: Section[];
  currentIndex: number;
  onIndexChange: (index: number) => void;
  onClose: () => void;
  canAccessIndex?: (index: number) => boolean;
  chapterTitle?: string;
}

export function SectionPresentationMode({
  sections,
  currentIndex,
  onIndexChange,
  onClose,
  canAccessIndex,
  chapterTitle,
}: SectionPresentationModeProps) {
  const currentSection = sections[currentIndex];
  const canNavigateTo = useCallback(
    (index: number) =>
      index >= 0 &&
      index < sections.length &&
      (canAccessIndex ? canAccessIndex(index) : true),
    [canAccessIndex, sections.length],
  );

  // Fetch diagrams for current section
  const { data: diagrams = [] } = useQuery({
    queryKey: ["section-diagrams", currentSection?.id],
    queryFn: () => diagramsApi.getSectionDiagrams(currentSection!.id),
    enabled: !!currentSection?.id,
    staleTime: 60000,
  });

  const completedDiagrams = diagrams.filter(
    (d) => d.status === "COMPLETED" && d.mermaidCode,
  );

  const goToNext = useCallback(() => {
    if (canNavigateTo(currentIndex + 1)) {
      onIndexChange(currentIndex + 1);
    }
  }, [canNavigateTo, currentIndex, onIndexChange]);

  const goToPrev = useCallback(() => {
    if (currentIndex > 0) {
      onIndexChange(currentIndex - 1);
    }
  }, [currentIndex, onIndexChange]);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      switch (e.key) {
        case "ArrowRight":
        case "ArrowDown":
        case " ":
          e.preventDefault();
          goToNext();
          break;
        case "ArrowLeft":
        case "ArrowUp":
          e.preventDefault();
          goToPrev();
          break;
        case "Escape":
          e.preventDefault();
          onClose();
          break;
        case "Home":
          e.preventDefault();
          if (canNavigateTo(0)) onIndexChange(0);
          break;
        case "End":
          e.preventDefault();
          if (canNavigateTo(sections.length - 1)) {
            onIndexChange(sections.length - 1);
          }
          break;
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [
    canNavigateTo,
    goToNext,
    goToPrev,
    onClose,
    onIndexChange,
    sections.length,
  ]);

  // Prevent body scroll while in presentation mode
  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "";
    };
  }, []);

  const progressPercent = ((currentIndex + 1) / sections.length) * 100;

  const content = (
    <div className="fixed inset-0 z-50 bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between px-6 py-3 bg-black/30">
        <div className="flex items-center gap-4">
          {chapterTitle && (
            <span className="text-white/70 text-sm">{chapterTitle}</span>
          )}
          <span className="text-white/50 text-sm">
            {currentIndex + 1} / {sections.length}
          </span>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={onClose}
          className="text-white/70 hover:text-white hover:bg-white/10"
        >
          <X className="h-5 w-5 mr-1" />
          Cerrar (ESC)
        </Button>
      </div>

      {/* Progress bar */}
      <div className="h-1 bg-black/30">
        <div
          className="h-full bg-primary-500 transition-all duration-300"
          style={{ width: `${progressPercent}%` }}
        />
      </div>

      {/* Main content area */}
      <div className="flex-1 flex items-center justify-center p-8 overflow-hidden">
        <div className="max-w-5xl w-full max-h-full overflow-y-auto bg-white rounded-2xl shadow-2xl">
          {/* Featured Image */}
          {currentSection?.featuredImageUrl && (
            <div className="w-full">
              <img
                src={currentSection.featuredImageUrl}
                alt={currentSection.title}
                className="w-full h-auto max-h-[350px] object-cover rounded-t-2xl"
              />
            </div>
          )}

          <div className="p-8">
            {/* Section Title */}
            <h1 className="text-4xl font-bold text-gray-900 mb-6">
              {currentSection?.title}
            </h1>

            {/* Audio Player */}
            {currentSection?.audioUrl && (
              <div className="mb-6 p-4 bg-gradient-to-r from-purple-50 to-indigo-50 rounded-xl">
                <div className="flex items-center gap-2 mb-2">
                  <Volume2 className="h-4 w-4 text-purple-600" />
                  <span className="text-sm font-medium text-purple-800">
                    Audio de la sección
                  </span>
                </div>
                <AudioPlayer
                  audioUrl={currentSection.audioUrl}
                  audioDuration={currentSection.audioDuration}
                  title={currentSection.title}
                />
              </div>
            )}

            {/* Content */}
            <div className="prose prose-lg max-w-none">
              <ContentRenderer html={currentSection?.content || ""} />
            </div>

            {/* Diagrams */}
            {completedDiagrams.length > 0 && (
              <div className="mt-8 space-y-6">
                <h3 className="text-lg font-semibold text-gray-800">
                  Diagramas
                </h3>
                {completedDiagrams.map((diagram) => (
                  <DiagramRenderer
                    key={diagram.id}
                    code={diagram.mermaidCode}
                    title={diagram.title}
                    showControls
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Navigation */}
      <div className="flex items-center justify-between px-6 py-4 bg-black/30">
        <Button
          variant="ghost"
          size="lg"
          onClick={goToPrev}
          disabled={currentIndex === 0}
          className="text-white/70 hover:text-white hover:bg-white/10 disabled:opacity-30"
        >
          <ChevronLeft className="h-6 w-6 mr-2" />
          Anterior
        </Button>

        {/* Section dots */}
        <div className="flex items-center gap-2">
          {sections.map((_, index) => {
            const isAccessible = canNavigateTo(index);
            return (
              <button
                key={index}
                onClick={() => isAccessible && onIndexChange(index)}
                disabled={!isAccessible}
                className={`w-2.5 h-2.5 rounded-full transition-all ${
                  index === currentIndex
                    ? "bg-primary-500 scale-125"
                    : !isAccessible
                      ? "bg-white/15 cursor-not-allowed"
                      : index < currentIndex
                        ? "bg-white/60"
                        : "bg-white/30"
                }`}
                title={`Ir a sección ${index + 1}`}
              />
            );
          })}
        </div>

        <Button
          variant="ghost"
          size="lg"
          onClick={goToNext}
          disabled={!canNavigateTo(currentIndex + 1)}
          className="text-white/70 hover:text-white hover:bg-white/10 disabled:opacity-30"
        >
          Siguiente
          <ChevronRight className="h-6 w-6 ml-2" />
        </Button>
      </div>

      {/* Keyboard shortcuts hint */}
      <div className="absolute bottom-20 left-1/2 -translate-x-1/2 text-white/40 text-xs">
        Usa las flechas ← → para navegar, ESC para salir
      </div>
    </div>
  );

  return createPortal(content, document.body);
}
